import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import App from './App';
import { store } from './store';
import { queryClient } from './services/queryClient';
import { SnackbarProvider } from './shared/contexts/SnackbarContext';
import { PermissionsProvider } from './shared/contexts/PermissionsContext';
import './index.css';

console.log('=== INDEX.TSX LOADED ===');
console.log('React version:', React.version);

// MUI Theme - indigo brand with cyan accent
const indigo = {
  main: '#4F46E5',
  light: '#818CF8',
  dark: '#4338CA',
  contrastText: '#ffffff',
  50: '#EEF2FF',
  100: '#E0E7FF',
  200: '#C7D2FE',
  300: '#A5B4FC',
  400: '#818CF8',
  500: '#4F46E5',
  600: '#4338CA',
  700: '#3730A3',
  800: '#312E81',
  900: '#1E1B4B',
};

const cyan = {
  main: '#06B6D4',
  light: '#22D3EE',
  dark: '#0891B2',
  contrastText: '#ffffff',
  50: '#ECFEFF',
  100: '#CFFAFE',
  200: '#A5F3FC',
  300: '#67E8F9',
  400: '#22D3EE',
  500: '#06B6D4',
  600: '#0891B2',
  700: '#0E7490',
  800: '#155E75',
  900: '#164E63',
};

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: indigo,
    secondary: cyan,
    info: cyan,
    success: {
      main: '#10B981',
      light: '#34D399',
      dark: '#059669',
      contrastText: '#ffffff',
      50: '#ECFDF5',
      100: '#D1FAE5',
      200: '#A7F3D0',
      300: '#6EE7B7',
      400: '#34D399',
      500: '#10B981',
      600: '#059669',
      700: '#047857',
      800: '#065F46',
      900: '#064E3B',
    },
    warning: {
      main: '#F59E0B',
      light: '#FBBF24',
      dark: '#D97706',
      contrastText: '#ffffff',
      50: '#FFFBEB',
      100: '#FEF3C7',
      200: '#FDE68A',
      300: '#FCD34D',
      400: '#FBBF24',
      500: '#F59E0B',
      600: '#D97706',
      700: '#B45309',
      800: '#92400E',
      900: '#78350F',
    },
    error: {
      main: '#EF4444',
      light: '#F87171',
      dark: '#DC2626',
      contrastText: '#ffffff',
      50: '#FEF2F2',
      100: '#FEE2E2',
      200: '#FECACA',
      300: '#FCA5A5',
      400: '#F87171',
      500: '#EF4444',
      600: '#DC2626',
      700: '#B91C1C',
      800: '#991B1B',
      900: '#7F1D1D',
    },
    background: {
      default: '#F8FAFC',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#0F172A',
      secondary: '#64748B',
    },
    divider: '#E2E8F0',
  },
  typography: {
    fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
    h1: {
      fontSize: '2rem',
      fontWeight: 600,
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h3: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: '0.875rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.8125rem',
      lineHeight: 1.5,
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '6px',
          padding: '8px 16px',
          fontWeight: 500,
        },
        contained: {
          boxShadow: 'none',
          color: '#ffffff',
          '&:hover': {
            boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
            color: '#ffffff',
          },
        },
        containedPrimary: {
          backgroundColor: '#4F46E5',
          '&:hover': {
            backgroundColor: '#4338CA',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
          border: '1px solid #E2E8F0',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '6px',
          fontWeight: 500,
        },
        sizeSmall: {
          height: '24px',
        },
        filledPrimary: {
          backgroundColor: '#4F46E5',
          color: '#ffffff',
          '& .MuiChip-icon': { color: '#ffffff' },
          '& .MuiChip-deleteIcon': { color: '#ffffff' },
        },
        filledSecondary: {
          backgroundColor: '#06B6D4',
          color: '#ffffff',
          '& .MuiChip-icon': { color: '#ffffff' },
          '& .MuiChip-deleteIcon': { color: '#ffffff' },
        },
        filledInfo: {
          backgroundColor: '#06B6D4',
          color: '#ffffff',
          '& .MuiChip-icon': { color: '#ffffff' },
          '& .MuiChip-deleteIcon': { color: '#ffffff' },
        },
        outlinedPrimary: {
          color: '#4F46E5',
          borderColor: '#4F46E5',
          backgroundColor: 'transparent',
          '& .MuiChip-icon': { color: '#4F46E5' },
        },
        outlinedSecondary: {
          color: '#06B6D4',
          borderColor: '#06B6D4',
          backgroundColor: 'transparent',
          '& .MuiChip-icon': { color: '#06B6D4' },
        },
        outlinedInfo: {
          color: '#06B6D4',
          borderColor: '#06B6D4',
          backgroundColor: 'transparent',
          '& .MuiChip-icon': { color: '#06B6D4' },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: '12px',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: '1px solid #E2E8F0',
        },
      },
    },
  },
});

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

console.log('=== MOUNTING REACT APP ===');
console.log('Root element:', document.getElementById('root'));

root.render(
  <React.StrictMode>
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <BrowserRouter>
              <SnackbarProvider>
                <PermissionsProvider>
                  <App />
                </PermissionsProvider>
              </SnackbarProvider>
            </BrowserRouter>
          </LocalizationProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </Provider>
  </React.StrictMode>
);
